import React from "react";
import styled from "styled-components";
import ReactDOMServer from "react-dom/server";
import { DownArrowIcon } from "./Icons";

const rawArrowSvg = ReactDOMServer.renderToStaticMarkup(
  <DownArrowIcon width={20} height={20} fill="#6C757D" />
);

const arrowDataUri = `data:image/svg+xml;utf8,${encodeURIComponent(
  rawArrowSvg
)}`;

const Pagination = ({
  currentPage,
  totalPages,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const maxPagesToShow = 5;
  const visiblePagesAround = 1;

  const pages = [];
  if (totalPages <= maxPagesToShow + 2) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > visiblePagesAround + 2) pages.push("...");
    const startPage = Math.max(2, currentPage - visiblePagesAround);
    const endPage = Math.min(totalPages - 1, currentPage + visiblePagesAround);
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    if (currentPage < totalPages - visiblePagesAround - 1) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <PaginationContainer>
      <CompactList>
        <PageItem
          className={currentPage === 1 ? "disabled" : ""}
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
        >
          &lt;
        </PageItem>
        {pages.map((page, idx) =>
          typeof page === "string" ? (
            <Ellipsis key={idx}>{page}</Ellipsis>
          ) : (
            <PageItem
              key={idx}
              $active={currentPage === page}
              onClick={() => page !== currentPage && onPageChange(page)}
            >
              {page}
            </PageItem>
          )
        )}
        <PageItem
          className={currentPage === totalPages ? "disabled" : ""}
          onClick={() =>
            currentPage < totalPages && onPageChange(currentPage + 1)
          }
        >
          &gt;
        </PageItem>
      </CompactList>

      <PaginationInfo>
        Items per page
        <select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
        >
          <option value={12}>12</option>
          <option value={24}>24</option>
          <option value={48}>48</option>
        </select>
        <span>
          {`${rowsPerPage * (currentPage - 1) + 1}-${Math.min(
            rowsPerPage * currentPage,
            totalRows
          )} of ${totalRows} results`}
        </span>
      </PaginationInfo>
    </PaginationContainer>
  );
};

export default Pagination;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin: 20px 0 12px;
  gap: 16px;

  /* —— mobile only: center everything and stack vertically —— */
  @media (max-width: 480px) {
    width: fit-content;
    flex-direction: column; /* nav above info */
    justify-content: center; /* center as a group */
    gap: 12px; /* a little tighter */
  }
`;

const PaginationList = styled.ul`
  display: flex;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    order: 1;
  }
`;

const CompactList = styled(PaginationList)`
  @media (max-width: 480px) {
    li {
      display: none;
    }
    li:first-child,
    li:last-child {
      display: block;
    }
  }
`;

const PageItem = styled.li`
  padding: 8px;
  width: 28px;
  text-align: center;
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-200, #f8f9fa);
  background: ${(props) => (props.$active ? "#0065c0" : "#f8f9fa")};
  color: ${(props) => (props.$active ? "#fff" : "#000")};
  transition: background-color 0.3s ease;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover {
    background-color: ${(props) => (props.$active ? "#0057a8" : "#e0e0e0")};
  }
`;

const Ellipsis = styled.li`
  margin: 0 5px;
  font-size: 18px;
  color: #999;
`;

const PaginationInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;

  select {
    padding: 6px 12px 6px 12px;
    width: 80px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--Colors-Neutral-400, #ced4da);
    background: #f8f9fa;
    flex-shrink: 0;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("${arrowDataUri}");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }

  span {
    white-space: nowrap;
    color: var(--Colors-Neutral-600, #6c757d);
  }
`;
